function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _instanceof(left, right) {
    "@swc/helpers - instanceof";
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { extent, merge, range } from "d3-array";
import { polygonArea, polygonCentroid, polygonContains } from "d3-polygon";
import polygonInside from "./polygonInside.js";
import polygonRayCast from "./polygonRayCast.js";
import polygonRotate from "./polygonRotate.js";
import simplify from "./simplify.js";
import pointDistanceSquared from "./pointDistanceSquared.js";
// Algorithm constants
var aspectRatioStep = 0.5; // step size for the aspect ratio
var angleStep = 5; // step size for angles (in degrees); has linear impact on running time
var polyCache = {};
/**
    Finds the largest rectangle that fits inside a given polygon, optimizing for area across configurable rotations and aspect ratios.

An angle of zero means that the longer side of the polygon (the width) will be aligned with the x axis. An angle of 90 and/or -90 means that the longer side of the polygon (the width) will be aligned with the y axis. The value can be a number between -90 and 90 specifying the angle of rotation of the polygon, a string which is parsed to a number, or an array of numbers specifying the possible rotations of the polygon.
    @author Daniel Smilkov [dsmilkov@gmail.com]
    @param poly An Array of points that represent a polygon.
    @param options An Object that allows for overriding various parameters of the algorithm.
    @param options.angle The allowed rotations of the final rectangle.
    @param options.aspectRatio The ratio between the width and height of the rectangle. The value can be a number, a string which is parsed to a number, or an array of numbers specifying the possible aspect ratios of the final rectangle.
    @param options.maxAspectRatio The maximum aspect ratio (width/height) allowed for the rectangle. This property should only be used if the aspectRatio is not provided.
    @param options.minAspectRatio The minimum aspect ratio (width/height) allowed for the rectangle. This property should only be used if the aspectRatio is not provided.
    @param options.nTries The number of randomly drawn points inside the polygon which the algorithm explores as possible center points of the maximal rectangle.
    @param options.minHeight The minimum height of the rectangle.
    @param options.minWidth The minimum width of the rectangle.
    @param options.tolerance The simplification tolerance factor, between 0 and 1. A larger tolerance corresponds to more extensive simplification.
    @param options.origin The center point of the rectangle. If specified, the rectangle will be fixed at that point, otherwise the algorithm optimizes across all possible points. The given value can be either a two dimensional array specifying the x and y coordinate of the origin or an array of two dimensional points specifying multiple possible center points of the rectangle.
    @param options.cache Whether or not to cache the result, which would be used in subsequent calculations to preserve consistency and speed up calculation time.
    @defaultValue
```
{
  angle: d3.range(-90, 95, 5),
  cache: true,
  maxAspectRatio: 15,
  minAspectRatio: 1,
  minHeight: 0,
  minWidth: 0,
  nTries: 20,
  tolerance: 0.02,
  verbose: false,
}
```
*/ export default function(poly) {
    var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    if (poly.length < 3) {
        if (options.verbose) console.error("polygon has to have at least 3 points", poly);
        return null;
    }
    // For visualization debugging purposes
    var events = [];
    // User's input normalization
    var opts = Object.assign({
        angle: range(-90, 90 + angleStep, angleStep),
        cache: true,
        maxAspectRatio: 15,
        minAspectRatio: 1,
        minHeight: 0,
        minWidth: 0,
        nTries: 20,
        tolerance: 0.02,
        verbose: false
    }, options);
    var angles = _instanceof(opts.angle, Array) ? opts.angle : typeof opts.angle === "number" ? [
        opts.angle
    ] : typeof opts.angle === "string" && !isNaN(Number(opts.angle)) ? [
        Number(opts.angle)
    ] : [];
    var aspectRatios = _instanceof(opts.aspectRatio, Array) ? opts.aspectRatio : typeof opts.aspectRatio === "number" ? [
        opts.aspectRatio
    ] : typeof opts.aspectRatio === "string" && !isNaN(Number(opts.aspectRatio)) ? [
        Number(opts.aspectRatio)
    ] : [];
    var origins = opts.origin && _instanceof(opts.origin, Array) ? _instanceof(opts.origin[0], Array) ? opts.origin : [
        opts.origin
    ] : [];
    var cacheString;
    if (opts.cache) {
        cacheString = merge(poly).join(",");
        cacheString += "-".concat(opts.minAspectRatio);
        cacheString += "-".concat(opts.maxAspectRatio);
        cacheString += "-".concat(opts.minHeight);
        cacheString += "-".concat(opts.minWidth);
        cacheString += "-".concat(angles.join(","));
        cacheString += "-".concat(origins.join(","));
        if (polyCache[cacheString]) return polyCache[cacheString];
    }
    var area = Math.abs(polygonArea(poly)); // take absolute value of the signed area
    if (area === 0) {
        if (opts.verbose) console.error("polygon has 0 area", poly);
        return null;
    }
    // get the width of the bounding box of the original polygon to determine tolerance
    var _extent = _sliced_to_array(extent(poly, function(d) {
        return d[0];
    }), 2), minx = _extent[0], maxx = _extent[1];
    var _extent1 = _sliced_to_array(extent(poly, function(d) {
        return d[1];
    }), 2), miny = _extent1[0], maxy = _extent1[1];
    // simplify polygon
    var tolerance = Math.min(maxx - minx, maxy - miny) * opts.tolerance;
    if (tolerance > 0) poly = simplify(poly, tolerance);
    if (opts.events) events.push({
        type: "simplify",
        poly: poly
    });
    var ref;
    // get the width of the bounding box of the simplified polygon
    ref = _sliced_to_array(extent(poly, function(d) {
        return d[0];
    }), 2), minx = ref[0], maxx = ref[1], ref;
    var ref1;
    ref1 = _sliced_to_array(extent(poly, function(d) {
        return d[1];
    }), 2), miny = ref1[0], maxy = ref1[1], ref1;
    var _ref = [
        maxx - minx,
        maxy - miny
    ], boxWidth = _ref[0], boxHeight = _ref[1];
    // discretize the binary search for optimal width to a resolution of this times the polygon width
    var widthStep = Math.min(boxWidth, boxHeight) / 50;
    // populate possible center points with random points inside the polygon
    if (!origins.length) {
        // get the centroid of the polygon
        var centroid = polygonCentroid(poly);
        if (!isFinite(centroid[0])) {
            if (opts.verbose) console.error("cannot find centroid", poly);
            return null;
        }
        if (polygonContains(poly, centroid)) origins.push(centroid);
        var nTries = opts.nTries;
        // get few more points inside the polygon
        while(nTries){
            var rndX = Math.random() * boxWidth + minx;
            var rndY = Math.random() * boxHeight + miny;
            var rndPoint = [
                rndX,
                rndY
            ];
            if (polygonContains(poly, rndPoint)) {
                origins.push(rndPoint);
            }
            nTries--;
        }
    }
    if (opts.events) events.push({
        type: "origins",
        points: origins
    });
    var maxArea = 0;
    var maxRect = null;
    for(var ai = 0; ai < angles.length; ai++){
        var angle = angles[ai];
        var angleRad = -angle * Math.PI / 180;
        if (opts.events) events.push({
            type: "angle",
            angle: angle
        });
        for(var i = 0; i < origins.length; i++){
            var origOrigin = origins[i];
            // generate improved origins
            var _polygonRayCast = _sliced_to_array(polygonRayCast(poly, origOrigin, angleRad), 2), p1W = _polygonRayCast[0], p2W = _polygonRayCast[1];
            var _polygonRayCast1 = _sliced_to_array(polygonRayCast(poly, origOrigin, angleRad + Math.PI / 2), 2), p1H = _polygonRayCast1[0], p2H = _polygonRayCast1[1];
            var modifOrigins = [];
            if (p1W && p2W) modifOrigins.push([
                (p1W[0] + p2W[0]) / 2,
                (p1W[1] + p2W[1]) / 2
            ]); // average along with width axis
            if (p1H && p2H) modifOrigins.push([
                (p1H[0] + p2H[0]) / 2,
                (p1H[1] + p2H[1]) / 2
            ]); // average along with height axis
            if (opts.events) events.push({
                type: "modifOrigin",
                idx: i,
                p1W: p1W,
                p2W: p2W,
                p1H: p1H,
                p2H: p2H,
                modifOrigins: modifOrigins
            });
            for(var i1 = 0; i1 < modifOrigins.length; i1++){
                var origin = modifOrigins[i1];
                if (opts.events) events.push({
                    type: "origin",
                    cx: origin[0],
                    cy: origin[1]
                });
                var _polygonRayCast2 = _sliced_to_array(polygonRayCast(poly, origin, angleRad), 2), p1W1 = _polygonRayCast2[0], p2W1 = _polygonRayCast2[1];
                if (p1W1 === null || p2W1 === null) continue;
                var minSqDistW = Math.min(pointDistanceSquared(origin, p1W1), pointDistanceSquared(origin, p2W1));
                var maxWidth = 2 * Math.sqrt(minSqDistW);
                var _polygonRayCast3 = _sliced_to_array(polygonRayCast(poly, origin, angleRad + Math.PI / 2), 2), p1H1 = _polygonRayCast3[0], p2H1 = _polygonRayCast3[1];
                if (p1H1 === null || p2H1 === null) continue;
                var minSqDistH = Math.min(pointDistanceSquared(origin, p1H1), pointDistanceSquared(origin, p2H1));
                var maxHeight = 2 * Math.sqrt(minSqDistH);
                if (maxWidth * maxHeight < maxArea) continue;
                var aRatios = aspectRatios;
                if (!aRatios.length) {
                    var minAspectRatio = Math.max(opts.minAspectRatio, opts.minWidth / maxHeight, maxArea / (maxHeight * maxHeight));
                    var maxAspectRatio = Math.min(opts.maxAspectRatio, maxWidth / opts.minHeight, maxWidth * maxWidth / maxArea);
                    aRatios = range(minAspectRatio, maxAspectRatio + aspectRatioStep, aspectRatioStep);
                }
                for(var a = 0; a < aRatios.length; a++){
                    var aRatio = aRatios[a];
                    // do a binary search to find the max width that works
                    var left = Math.max(opts.minWidth, Math.sqrt(maxArea * aRatio));
                    var right = Math.min(maxWidth, maxHeight * aRatio);
                    if (right * maxHeight < maxArea) continue;
                    if (opts.events && right - left >= widthStep) events.push({
                        type: "aRatio",
                        aRatio: aRatio
                    });
                    while(right - left >= widthStep){
                        var width = (left + right) / 2;
                        var height = width / aRatio;
                        var _origin = _sliced_to_array(origin, 2), cx = _origin[0], cy = _origin[1];
                        var rectPoly = [
                            [
                                cx - width / 2,
                                cy - height / 2
                            ],
                            [
                                cx + width / 2,
                                cy - height / 2
                            ],
                            [
                                cx + width / 2,
                                cy + height / 2
                            ],
                            [
                                cx - width / 2,
                                cy + height / 2
                            ]
                        ];
                        rectPoly = polygonRotate(rectPoly, angleRad, origin);
                        var insidePoly = polygonInside(rectPoly, poly);
                        if (insidePoly) {
                            // we know that the area is already greater than the maxArea found so far
                            maxArea = width * height;
                            rectPoly.push(rectPoly[0]);
                            maxRect = {
                                area: maxArea,
                                cx: cx,
                                cy: cy,
                                width: width,
                                height: height,
                                angle: -angle,
                                points: rectPoly
                            };
                            left = width; // increase the width in the binary search
                        } else {
                            right = width; // decrease the width in the binary search
                        }
                        if (opts.events) events.push({
                            type: "rectangle",
                            areaFraction: width * height / area,
                            cx: cx,
                            cy: cy,
                            width: width,
                            height: height,
                            angle: angle,
                            insidePoly: insidePoly
                        });
                    }
                }
            }
        }
    }
    if (opts.cache) {
        polyCache[cacheString] = maxRect;
    }
    return opts.events ? Object.assign(maxRect || {}, {
        events: events
    }) : maxRect;
}

//# sourceMappingURL=largestRect.js.map